import React, { lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useSearchParams, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/AuthContext";
import {
  AdminCommandPaletteProvider, AdminCommandPaletteFAB,
} from "./shared/components/AdminCommandPalette";
import Header from "./pages/Header";
import Dashboard from "./pages/Dashboard";
import PropertyDetail from "./pages/PropertyDetail";
import MapView from "./pages/MapView";
import AdminPage from "./pages/AdminPage";
import Login from "./pages/Login";
import AccessHouseEntry from "./pages/AccessHouseEntry";
import Register from "./pages/Register";
import Pricing from "./pages/Pricing";
import DealerDashboard from "./pages/DealerDashboard";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ShortlistPublic from "./pages/ShortlistPublic";
import ShortlistPrint from "./pages/ShortlistPrint";
import MagicLogin from "./pages/MagicLogin";
import AuthCallback from "./pages/AuthCallback";
import CompleteProfile from "./pages/CompleteProfile";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetail from "./pages/ProjectDetail";
import TeamLogin from "./pages/TeamLogin";
// v2.71.50 — TeamDashboard.jsx is deprecated. The /team/* tree now uses
// dedicated focused pages under TeamLayout. Keep the import commented for
// safety; can be deleted once a full deploy + smoke is confirmed.
// import TeamDashboard from "./pages/TeamDashboard";
import SavedProperties from "./pages/SavedProperties";
import Profile from "./pages/Profile";
import MyAccount from "./pages/MyAccount";
import MyEnquiries from "./pages/MyEnquiries";
import MyAlerts from "./pages/MyAlerts";
import MyRecentlyViewed from "./pages/MyRecentlyViewed";
import DealerOverview from "./pages/DealerOverview";
import AdminOverview from "./pages/AdminOverview";
import AdminUserProfile from "./pages/AdminUserProfile";
import PlansPreview from "./pages/PlansPreview";
import Page from "./pages/Page";
import Footer from "./pages/Footer";
import ScrollToTop from "./components/ScrollToTop";
import MobileBottomNav from "./components/MobileBottomNav";
// v2.71.67 — SmartEnquiryPopup removed (enquiry system retired in favour of
// callback-only CRM flow). Behavioural lead tracking is preserved via
// `recordView` in the store and the AuthGate sign-in prompt.
import PostSignupPlansModal from "./components/PostSignupPlansModal";
// v2.71.51 — FloatingBackButton retired. Pages now use the integrated
// <PageHeader /> component from /shared/components/PageHeader.jsx.
// import FloatingBackButton from "./components/FloatingBackButton";
import { BrandProvider } from "./lib/BrandContext";
import { ConfigProvider } from "./lib/ConfigContext";
// v2.71.77 — Global error boundaries. RootErrorBoundary wraps the whole
// app (last-resort) and PageErrorBoundary wraps each high-risk route so a
// single page crash never white-screens the whole shell. Also handles
// stale-chunk errors (post-deploy white screen).
import { RootErrorBoundary, PageErrorBoundary } from "./shared/components/ErrorBoundary";

// v2.71.32 — Route-based dashboard migration. Every admin/dealer tab now
// has its own URL. Legacy `?tab=` URLs redirect to the new routes so
// existing bookmarks continue to work.
import {
  AdminListings, AdminApprovals, AdminUsers, AdminDealers, AdminAddClient,
  AdminCRM, AdminPlans, AdminContent, AdminPages, AdminTransactions,
  AdminSettings, AdminProjects, AdminTeam, AdminFilters, AdminLocations,
  AdminTestimonials, AdminShortlists, AdminNotifications, AdminBackupCenter,
  ADMIN_TAB_TO_ROUTE,
} from "./pages/admin/routes";
import {
  DealerSubmit, DealerListings, DealerLeads, DealerEarnings, DealerKYC,
  DealerProjects, DealerExploreRoute, DealerPlan, DEALER_TAB_TO_ROUTE,
} from "./pages/dealer/routes";
import {
  DealerSavedRoute, DealerShortlistsRoute, DealerNotificationsRoute,
  DealerAnalyticsRoute, DealerProfileRoute,
} from "./pages/dealer/placeholders";
// v2.71.43 — Builder-focused console (v2.71.65 — adds Listings + Marketplace + Add choice)
import {
  BuilderOverviewRoute, BuilderInquiriesRoute, BuilderEntry,
  BuilderListingsRoute, BuilderMarketplaceRoute, BuilderAddRoute,
} from "./pages/builder/routes";
import {
  BuilderProjectsRoute, BuilderBrochuresRoute, BuilderUpdatesRoute,
  BuilderVisitsRoute, BuilderAnalyticsRoute, BuilderNotificationsRoute,
  BuilderProfileRoute,
} from "./pages/builder/placeholders";
// v2.71.50 — Legacy /app/frontend/src/pages/team/routes.jsx is no longer
// the source of truth. The /team/* tree uses dedicated focused page files
// mounted under TeamLayout below.
const CallerWorkflow = lazy(() => import("./pages/team/CallerWorkflow"));
const FieldAgent = lazy(() => import("./pages/team/FieldAgent"));
const TeamLayout = lazy(() => import("./pages/team/TeamLayout"));
const TeamHome = lazy(() => import("./pages/team/TeamHome"));
const TeamPropertiesPage = lazy(() => import("./pages/team/TeamPropertiesPage"));
const TeamMapPage = lazy(() => import("./pages/team/TeamMapPage"));
const TeamShortlistsPage = lazy(() => import("./pages/team/TeamShortlistsPage"));
const TeamShortlistDetailPage = lazy(() => import("./pages/team/TeamShortlistDetailPage"));
const TeamCallbacksPage = lazy(() => import("./pages/team/TeamCallbacksPage"));

// v2.71.88 — Office Presentation System. Strictly isolated under `/office/*`.
// Lazy-loaded so the bundle size for the public site is unaffected.
const OfficeLayout = lazy(() => import("./pages/office/OfficeLayout"));
const OfficeHome = lazy(() => import("./pages/office/OfficeHome"));
const OfficeBrowse = lazy(() => import("./pages/office/OfficeBrowse"));
const OfficeDecks = lazy(() => import("./pages/office/OfficeDecks"));
const OfficePresent = lazy(() => import("./pages/office/OfficePresent"));
const OfficeProperty = lazy(() => import("./pages/office/OfficeProperty"));
const OfficeCompare = lazy(() => import("./pages/office/OfficeCompare"));
const OfficeShortlist = lazy(() => import("./pages/office/OfficeShortlist"));
// Phase II — Sprint 1 — Inventory Intelligence.
const OfficeInventory = lazy(() => import("./pages/office/OfficeInventory"));

/** v2.71.32 — Translate legacy `?tab=X` query → new `/<scope>/X` route.
 *  Returns a `<Navigate>` element when redirection is needed, otherwise null. */
function legacyTabRedirect(currentSearch, tabMap) {
  const params = new URLSearchParams(currentSearch);
  const tab = params.get("tab");
  if (!tab) return null;
  const dest = tabMap[tab];
  if (!dest) return null;
  // Preserve any other query params (e.g. `?tab=submissions&filter=sold`).
  params.delete("tab");
  const remaining = params.toString();
  return <Navigate to={remaining ? `${dest}?${remaining}` : dest} replace />;
}

function AdminEntry() {
  const [params] = useSearchParams();
  const redirect = legacyTabRedirect(params.toString(), ADMIN_TAB_TO_ROUTE);
  if (redirect) return redirect;
  // No tab param at all → land on the new overview.
  return <Navigate to="/admin/overview" replace />;
}

function DealerEntry() {
  const [params] = useSearchParams();
  const redirect = legacyTabRedirect(params.toString(), DEALER_TAB_TO_ROUTE);
  if (redirect) return redirect;
  return <Navigate to="/dealer/overview" replace />;
}

function App() {
  return (
    <div className="App">
      <RootErrorBoundary>
      <AuthProvider>
        <ConfigProvider>
        <BrandProvider>
          <BrowserRouter>
          <AdminCommandPaletteProvider>
          <ScrollToTop />
          <Header />
          <AppRoutes />
          <Footer />
          <MobileBottomNav />
          {/* v2.71.51 — FloatingBackButton retired. Pages now use the
              integrated <PageHeader /> component from shared/components/.
              v2.71.67 — SmartEnquiryPopup retired (enquiry system removed). */}
          <PostSignupPlansModal />
          <AdminCommandPaletteFAB />
          <Toaster position="top-right" richColors />
          </AdminCommandPaletteProvider>
        </BrowserRouter>
        </BrandProvider>
        </ConfigProvider>
      </AuthProvider>
      </RootErrorBoundary>
    </div>
  );
}

function AppRoutes() {
  // Google OAuth returns to `<origin>/#session_id=<one_time>`. Detect the
  // fragment synchronously during render (before any Route mounts) so no
  // page fetches /auth/me before the session cookie is established.
  const location = useLocation();
  if (typeof window !== "undefined" && location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={
        <PageErrorBoundary scope="dashboard"><Dashboard /></PageErrorBoundary>
      } />
      <Route path="/property/:id" element={
        <PageErrorBoundary scope="property-detail"><PropertyDetail /></PageErrorBoundary>
      } />
      <Route path="/map" element={
        <PageErrorBoundary scope="map-view"><MapView /></PageErrorBoundary>
      } />
      <Route path="/login" element={<Login />} />
      <Route path="/access" element={<AccessHouseEntry />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/magic" element={<MagicLogin />} />
      <Route path="/complete-profile" element={<CompleteProfile />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/dealer" element={<DealerEntry />} />
      <Route path="/builder" element={<BuilderEntry />} />
      <Route path="/builder/overview" element={<BuilderOverviewRoute />} />
      <Route path="/builder/projects" element={<BuilderProjectsRoute />} />
      <Route path="/builder/listings" element={<BuilderListingsRoute />} />
      <Route path="/builder/add" element={<BuilderAddRoute />} />
      <Route path="/builder/marketplace" element={<BuilderMarketplaceRoute />} />
      <Route path="/builder/inquiries" element={<BuilderInquiriesRoute />} />
      <Route path="/builder/brochures" element={<BuilderBrochuresRoute />} />
      <Route path="/builder/updates" element={<BuilderUpdatesRoute />} />
      <Route path="/builder/visits" element={<BuilderVisitsRoute />} />
      <Route path="/builder/analytics" element={<BuilderAnalyticsRoute />} />
      <Route path="/builder/notifications" element={<BuilderNotificationsRoute />} />
      <Route path="/builder/profile" element={<BuilderProfileRoute />} />
      <Route path="/admin" element={<AdminEntry />} />
      <Route path="/projects" element={<ProjectsPage />} />
      <Route path="/project/:id" element={<ProjectDetail />} />
      <Route path="/team-login" element={<TeamLogin />} />
      <Route path="/team" element={
        <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
          <TeamLayout />
        </Suspense>
      }>
        <Route index element={
          <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
            <TeamHome />
          </Suspense>
        } />
        <Route path="workflow" element={
          <Suspense fallback={<div className="p-6 text-slate-500">Loading workflow…</div>}>
            <CallerWorkflow />
          </Suspense>
        } />
        <Route path="field" element={
          <Suspense fallback={<div className="p-6 text-slate-500">Loading visits…</div>}>
            <FieldAgent />
          </Suspense>
        } />
        <Route path="leads" element={<Navigate to="/team/workflow" replace />} />
        <Route path="visits" element={<Navigate to="/team/field" replace />} />
        <Route path="properties" element={
          <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
            <TeamPropertiesPage />
          </Suspense>
        } />
        <Route path="map" element={
          <Suspense fallback={<div className="p-6 text-slate-500">Loading map…</div>}>
            <TeamMapPage />
          </Suspense>
        } />
        <Route path="shortlists" element={
          <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
            <TeamShortlistsPage />
          </Suspense>
        } />
        <Route path="shortlists/:sid" element={
          <Suspense fallback={<div className="p-6 text-slate-500">Loading shortlist…</div>}>
            <TeamShortlistDetailPage />
          </Suspense>
        } />
        <Route path="callbacks" element={
          <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
            <TeamCallbacksPage />
          </Suspense>
        } />
      </Route>
      {/* v2.71.88 — Office Presentation System. Strictly isolated. */}
      <Route path="/office" element={
        <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
          <PageErrorBoundary scope="office"><OfficeLayout /></PageErrorBoundary>
        </Suspense>
      }>
        <Route index element={<Suspense fallback={null}><OfficeHome /></Suspense>} />
        <Route path="browse" element={<Suspense fallback={null}><OfficeBrowse /></Suspense>} />
        <Route path="property/:id" element={<Suspense fallback={null}><OfficeProperty /></Suspense>} />
        <Route path="compare" element={<Suspense fallback={null}><OfficeCompare /></Suspense>} />
        <Route path="shortlist" element={<Suspense fallback={null}><OfficeShortlist /></Suspense>} />
        <Route path="decks" element={<Suspense fallback={null}><OfficeDecks /></Suspense>} />
        <Route path="decks/:id" element={<Suspense fallback={null}><OfficeDecks /></Suspense>} />
        <Route path="present/:id" element={<Suspense fallback={null}><OfficePresent /></Suspense>} />
        {/* Phase II — Sprint 1 — Inventory Intelligence */}
        <Route path="inventory" element={<Suspense fallback={null}><OfficeInventory /></Suspense>} />
      </Route>
      <Route path="/shortlist/:slug" element={<ShortlistPublic />} />
      <Route path="/shortlist/:slug/print" element={<ShortlistPrint />} />
      <Route path="/saved" element={<SavedProperties />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/account" element={<MyAccount />} />
      <Route path="/account/enquiries" element={<MyEnquiries />} />
      <Route path="/account/alerts" element={<MyAlerts />} />
      <Route path="/account/recent" element={<MyRecentlyViewed />} />
      <Route path="/dealer/overview" element={<DealerOverview />} />
      <Route path="/dealer/listings" element={<DealerListings />} />
      <Route path="/dealer/leads" element={<DealerLeads />} />
      <Route path="/dealer/submit" element={<DealerSubmit />} />
      <Route path="/dealer/earnings" element={<DealerEarnings />} />
      <Route path="/dealer/kyc" element={<DealerKYC />} />
      <Route path="/dealer/projects" element={<DealerProjects />} />
      <Route path="/dealer/explore" element={<DealerExploreRoute />} />
      <Route path="/dealer/plan" element={<DealerPlan />} />
      <Route path="/dealer/saved" element={<DealerSavedRoute />} />
      <Route path="/dealer/shortlists" element={<DealerShortlistsRoute />} />
      <Route path="/dealer/notifications" element={<DealerNotificationsRoute />} />
      <Route path="/dealer/analytics" element={<DealerAnalyticsRoute />} />
      <Route path="/dealer/profile" element={<DealerProfileRoute />} />
      <Route path="/admin/overview" element={<AdminOverview />} />
      <Route path="/admin/listings" element={<AdminListings />} />
      <Route path="/admin/approvals" element={<AdminApprovals />} />
      <Route path="/admin/users" element={<AdminUsers />} />
      <Route path="/admin/users/:id" element={<AdminUserProfile />} />
      <Route path="/admin/dealers" element={<AdminDealers />} />
      <Route path="/admin/add-client" element={<AdminAddClient />} />
      <Route path="/admin/crm" element={<AdminCRM />} />
      <Route path="/admin/leads" element={<Navigate to="/admin/crm" replace />} />
      <Route path="/admin/enquiries" element={<Navigate to="/admin/crm" replace />} />
      <Route path="/admin/plans" element={<AdminPlans />} />
      <Route path="/admin/content" element={<AdminContent />} />
      <Route path="/admin/pages" element={<AdminPages />} />
      <Route path="/admin/transactions" element={<AdminTransactions />} />
      <Route path="/admin/settings" element={<AdminSettings />} />
      <Route path="/admin/projects" element={<AdminProjects />} />
      <Route path="/admin/team" element={<AdminTeam />} />
      <Route path="/admin/filters" element={<AdminFilters />} />
      <Route path="/admin/locations" element={<AdminLocations />} />
      <Route path="/admin/testimonials" element={<AdminTestimonials />} />
      <Route path="/admin/shortlists" element={<AdminShortlists />} />
      <Route path="/admin/notifications" element={<AdminNotifications />} />
      <Route path="/admin/backup" element={<AdminBackupCenter />} />
      <Route path="/plans-preview" element={<PlansPreview />} />
      <Route path="/page/:slug" element={<Page />} />
    </Routes>
  );
}

export default App;
